import { getNotasLocal, saveNotaLocal, getRecentCotacoes } from '../db.js';
import { escapeHTML } from '../security.js';
import { authManager, getSavedScope, setSavedScope } from '../auth-manager.js';
import { DEFAULT_CATEGORIES, getCategoryIcon } from '../categorizer.js';
import { openQRModal } from './qr-modal.js';

const CATEGORY_COLOR_MAP = {
  'Padaria': '#F59E0B',
  'Carnes': '#EF4444',
  'Carnes & Peixes': '#EF4444',
  'Frios & Embutidos': '#FB923C',
  'Hortifruti': '#10B981',
  'Laticínios': '#3B82F6',
  'Laticínios & Queijos': '#3B82F6',
  'Doces/Snacks': '#8B5CF6',
  'Doces & Biscoitos': '#8B5CF6',
  'Matinais & Cereais': '#D97706',
  'Higiene': '#06B6D4',
  'Higiene Pessoal': '#06B6D4',
  'Bebidas': '#F97316',
  'Mercearia': '#6366F1',
  'Mercearia & Condimentos': '#6366F1',
  'Limpeza': '#14B8A6',
  'Limpeza Doméstica': '#14B8A6',
  'Pet Shop': '#A855F7',
  'Restaurante': '#F97316',
  'Outros': '#94A3B8'
};

const CATEGORY_ICON_NAME_MAP = {
  'Padaria': 'bakery_dining',
  'Carnes': 'set_meal',
  'Carnes & Peixes': 'set_meal',
  'Frios & Embutidos': 'lunch_dining',
  'Hortifruti': 'nutrition',
  'Laticínios': 'egg',
  'Laticínios & Queijos': 'egg',
  'Doces/Snacks': 'cookie',
  'Doces & Biscoitos': 'cookie',
  'Matinais & Cereais': 'breakfast_dining',
  'Higiene': 'soap',
  'Higiene Pessoal': 'soap',
  'Bebidas': 'local_bar',
  'Mercearia': 'shopping_bag',
  'Mercearia & Condimentos': 'shopping_bag',
  'Limpeza': 'cleaning_services',
  'Limpeza Doméstica': 'cleaning_services',
  'Pet Shop': 'pets',
  'Restaurante': 'restaurant',
  'Outros': 'receipt_long'
};

let isBalanceHidden = localStorage.getItem('nfce_hide_balance') === 'true';

export async function renderHomeView(container, param, syncManager) {
  let currentScope = getSavedScope();
  let notas = [];
  const user = authManager.getUser();

  try {
    if (authManager.isAuthenticated()) {
      const res = await authManager.fetchWithAuth(`/api/notas?scope=${currentScope}&full=true`);
      if (res.ok) {
        notas = await res.json();
      } else {
        notas = await getNotasLocal();
      }
    } else {
      notas = await getNotasLocal();
    }
  } catch (e) {
    notas = await getNotasLocal();
  }

  const isPersonal = currentScope === 'personal';
  const isFamily = currentScope === 'family';

  // Compute Metrics for Statistics Cards
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  const currentMonthName = monthNames[currentMonth];

  // 1. Total Balance
  const totalBalance = notas.reduce((sum, n) => sum + (n.totais?.valorTotal || n.valorTotal || 0), 0);
  const numPurchases = notas.length;
  const avgPurchase = numPurchases > 0 ? totalBalance / numPurchases : 0;

  // 2. Current Month Spending & Categories
  const currentMonthNotas = notas.filter(n => {
    if (!n.dataEmissao) return false;
    const d = new Date(n.dataEmissao);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
  });
  const monthlySpend = currentMonthNotas.reduce((sum, n) => sum + (n.totais?.valorTotal || n.valorTotal || 0), 0);

  // Category breakdown for current month (or all notas if month is empty)
  const targetCategoryNotas = currentMonthNotas.length > 0 ? currentMonthNotas : notas;
  const monthCategoryMap = {};
  targetCategoryNotas.forEach(n => {
    (n.itens || []).forEach(item => {
      const cat = item.categoriaNome || 'Outros';
      monthCategoryMap[cat] = (monthCategoryMap[cat] || 0) + (item.valorTotal || 0);
    });
  });

  const sortedMonthCategories = Object.entries(monthCategoryMap).sort((a, b) => b[1] - a[1]);
  const monthCatTotalSum = sortedMonthCategories.reduce((sum, [, val]) => sum + val, 0) || 1;
  const top3MonthCategories = sortedMonthCategories.slice(0, 3);

  // Overall Top Category (All time)
  const allCategoryMap = {};
  notas.forEach(n => {
    (n.itens || []).forEach(item => {
      const cat = item.categoriaNome || 'Outros';
      allCategoryMap[cat] = (allCategoryMap[cat] || 0) + (item.valorTotal || 0);
    });
  });

  let topCategoryName = 'Nenhuma';
  let topCategoryValue = 0;
  Object.entries(allCategoryMap).forEach(([cat, val]) => {
    if (val > topCategoryValue) {
      topCategoryValue = val;
      topCategoryName = cat;
    }
  });
  const topCatIcon = CATEGORY_ICON_NAME_MAP[topCategoryName] || 'category';
  const topCatColor = CATEGORY_COLOR_MAP[topCategoryName] || '#3B82F6';

  // 3. Same-Store Price Hikes Detection (Identical to Analise Pilar Preços)
  const exactProductPricesMap = {};
  notas.forEach(n => {
    const rawStore = n.emitente?.razaoSocial || n.razaoSocial || 'Estabelecimento';
    const storeName = rawStore.toLowerCase().replace(/(?:^|\s)\S/g, a => a.toUpperCase());
    (n.itens || []).forEach(i => {
      let desc = i.descricao || '';
      let cleanDesc = desc
        .replace(/\s*Vl\.?\s*Total.*$/i, '')
        .replace(/\s*Total\s*[\d,\.]+/i, '')
        .replace(/\s*Vl\.?\s*[\d,\.]+/i, '')
        .replace(/\s*-\s*R\$.*$/i, '')
        .replace(/\s*R\$\s*[\d,\.]+/i, '')
        .trim();
      const matchKey = cleanDesc.toLowerCase().replace(/[^a-z0-9]/g, '');
      const itemVal = i.valorTotal || 0;
      const itemQty = i.quantidade || 1;
      const itemUnitVal = itemQty > 0 ? (itemVal / itemQty) : itemVal;

      if (!exactProductPricesMap[matchKey]) {
        exactProductPricesMap[matchKey] = {
          displayName: cleanDesc,
          purchases: []
        };
      }
      exactProductPricesMap[matchKey].purchases.push({
        store: storeName,
        unitPrice: itemUnitVal,
        totalPrice: itemVal,
        qty: itemQty,
        rawDate: n.dataEmissao ? new Date(n.dataEmissao).getTime() : 0,
      });
    });
  });

  const priceHikesSameStore = [];
  Object.values(exactProductPricesMap).forEach(itemData => {
    const purchases = itemData.purchases.sort((a, b) => a.rawDate - b.rawDate);
    if (purchases.length >= 2) {
      const storeMap = {};
      purchases.forEach(p => {
        if (!storeMap[p.store]) storeMap[p.store] = [];
        storeMap[p.store].push(p);
      });

      Object.entries(storeMap).forEach(([store, storePurchases]) => {
        if (storePurchases.length >= 2) {
          const earliest = storePurchases[0];
          const latest = storePurchases[storePurchases.length - 1];
          const diff = latest.unitPrice - earliest.unitPrice;
          if (diff > 0.05) {
            const pctIncrease = parseInt(((diff / earliest.unitPrice) * 100).toFixed(0), 10);
            priceHikesSameStore.push({
              prodName: itemData.displayName,
              storeName: store,
              oldPrice: earliest.unitPrice,
              newPrice: latest.unitPrice,
              priceDiff: diff,
              pctIncrease
            });
          }
        }
      });
    }
  });

  const totalHikeDiff = priceHikesSameStore.reduce((sum, h) => sum + h.priceDiff, 0);
  const maxHikePct = priceHikesSameStore.length > 0 
    ? Math.max(...priceHikesSameStore.map(h => h.pctIncrease)) 
    : 0;

  // User presentation
  const displayName = user?.name || user?.email?.split('@')[0] || 'Usuário';
  const userPhoto = user?.picture;
  const userInitial = displayName.charAt(0).toUpperCase();

  // Recent 2 transactions
  const recentNotas = [...notas].sort((a, b) => {
    const da = a.dataEmissao ? new Date(a.dataEmissao).getTime() : 0;
    const db = b.dataEmissao ? new Date(b.dataEmissao).getTime() : 0;
    return db - da;
  }).slice(0, 2);

  container.innerHTML = `
    <!-- Section Resumo / Estatísticas Header -->
    <div class="home-section-title-row">
      <h3 class="home-section-title">Resumo</h3>
      
      <!-- Scope Pill Switcher -->
      <div class="home-scope-switcher" id="home-scope-switcher">
        <button class="home-scope-pill ${isPersonal ? 'active' : ''}" data-scope="personal">Pessoal</button>
        <button class="home-scope-pill ${isFamily ? 'active' : ''}" data-scope="family">Família</button>
      </div>
    </div>

    <!-- Summary Carousel Cards (Rectangular/Square Cards) -->
    <div class="home-carousel-wrapper">
      <div class="home-carousel-container" id="home-carousel">
        
        <!-- 1. Total Acumulado -->
        <div class="home-kpi-card home-card-balance">
          <div class="home-card-header">
            <span class="home-card-label">Total Acumulado</span>
            <button class="home-card-action-btn" id="toggle-balance-visibility" title="Ocultar/Exibir Valores">
              <span class="material-symbols-outlined" id="balance-eye-icon">${isBalanceHidden ? 'visibility_off' : 'visibility'}</span>
            </button>
          </div>
          
          <div class="home-card-value font-mono money-val ${isBalanceHidden ? 'balance-hidden' : ''}" id="total-balance-value">
            ${isBalanceHidden ? '••••••••' : `R$ ${totalBalance.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </div>

          <div class="home-card-footer">
            <span class="home-card-sublabel">Total registrado:</span>
            <span class="home-card-subval">${numPurchases} ${numPurchases === 1 ? 'nota fiscal' : 'notas fiscais'}</span>
          </div>
        </div>

        ${priceHikesSameStore.length > 0 ? `
          <!-- Card: Alta de Preços Detectada (Exibido se houver altas) -->
          <div class="home-kpi-card home-card-hike" id="home-card-price-hikes" style="cursor: pointer;" title="Clique para ver detalhes em Análise de Preços">
            <div class="home-card-header">
              <span class="home-card-label" style="color: #DC2626; font-weight: 700; display: flex; align-items: center; gap: 0.35rem;">
                <span class="material-symbols-outlined" style="font-size: 16px; color: #DC2626;">trending_up</span>
                Alta de Preços
              </span>
              <span class="material-symbols-outlined" style="font-size: 1.15rem; color: #DC2626;">arrow_forward</span>
            </div>

            <div class="home-card-value font-mono money-val ${isBalanceHidden ? 'balance-hidden' : ''}" style="color: #DC2626;" id="price-hike-value">
              ${isBalanceHidden ? '••••••••' : `+ R$ ${totalHikeDiff.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </div>

            <div class="home-card-footer">
              <span class="home-card-sublabel" style="color: #DC2626; font-weight: 600;">▲ +${maxHikePct}% máx:</span>
              <span class="home-card-subval">${priceHikesSameStore.length} ${priceHikesSameStore.length === 1 ? 'item subiu' : 'itens subiram'}</span>
            </div>
          </div>
        ` : ''}

        <!-- 2. Gastos do Mês por Categoria (As 3 Maiores) -->
        <div class="home-kpi-card home-card-spend">
          <div class="home-card-header">
            <span class="home-card-label">Gastos em ${currentMonthName}</span>
          </div>

          <div class="home-card-value font-mono money-val ${isBalanceHidden ? 'balance-hidden' : ''}" id="monthly-spend-value">
            ${isBalanceHidden ? '••••••••' : `R$ ${monthlySpend.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </div>

          <!-- Segmented Category Progress Bar -->
          <div class="home-category-segmented-bar">
            ${top3MonthCategories.length === 0 ? `
              <div class="home-segment-empty"></div>
            ` : top3MonthCategories.map(([cat, val]) => {
              const pct = Math.max(6, Math.round((val / monthCatTotalSum) * 100));
              const color = CATEGORY_COLOR_MAP[cat] || '#94A3B8';
              return `<div class="home-segment" style="width: ${pct}%; background-color: ${color};" title="${escapeHTML(cat)}: ${pct}%"></div>`;
            }).join('')}
          </div>

          <!-- Category Legend Bullets (Top 3) -->
          <div class="home-category-legend">
            ${top3MonthCategories.length === 0 ? `
              <div class="home-category-legend-empty">Nenhum gasto registrado este mês</div>
            ` : top3MonthCategories.map(([cat, val]) => {
              const color = CATEGORY_COLOR_MAP[cat] || '#94A3B8';
              const pct = Math.round((val / monthCatTotalSum) * 100);
              return `
                <div class="home-legend-item">
                  <span class="home-legend-dot" style="background-color: ${color};"></span>
                  <span class="home-legend-name">${escapeHTML(cat)}</span>
                  <span class="home-legend-pct">${pct}%</span>
                </div>
              `;
            }).join('')}
          </div>
        </div>

        <!-- 3. Compras Realizadas -->
        <div class="home-kpi-card home-card-purchases">
          <div class="home-card-header">
            <span class="home-card-label">Compras Realizadas</span>
            <span class="material-symbols-outlined text-muted" style="font-size: 1.15rem;">shopping_bag</span>
          </div>

          <div class="home-card-value">
            ${numPurchases}
          </div>

          <div class="home-card-footer">
            <span class="home-card-sublabel">Histórico de compras:</span>
            <span class="home-card-subval">${isPersonal ? 'Notas pessoais' : 'Notas da família'}</span>
          </div>
        </div>

        <!-- 4. Ticket Médio -->
        <div class="home-kpi-card home-card-ticket">
          <div class="home-card-header">
            <span class="home-card-label">Ticket Médio</span>
            <span class="material-symbols-outlined text-muted" style="font-size: 1.15rem;">receipt_long</span>
          </div>

          <div class="home-card-value font-mono money-val ${isBalanceHidden ? 'balance-hidden' : ''}" id="avg-ticket-value">
            ${isBalanceHidden ? '••••••••' : `R$ ${avgPurchase.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </div>

          <div class="home-card-footer">
            <span class="home-card-sublabel">Média por cupom:</span>
            <span class="home-card-subval">por nota fiscal</span>
          </div>
        </div>

        <!-- 5. Maior Categoria -->
        <div class="home-kpi-card home-card-top-cat">
          <div class="home-card-header">
            <span class="home-card-label">Maior Categoria</span>
            <span class="material-symbols-outlined" style="font-size: 1.25rem; color: ${topCatColor};">${topCatIcon}</span>
          </div>

          <div class="home-card-value" style="font-size: 1.25rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${escapeHTML(topCategoryName)}">
            ${escapeHTML(topCategoryName)}
          </div>

          <div class="home-card-footer">
            <span class="home-card-sublabel">Total acumulado:</span>
            <span class="home-card-subval money-val ${isBalanceHidden ? 'balance-hidden' : ''}" id="top-cat-value" style="color: ${topCatColor};">
              ${isBalanceHidden ? '••••••••' : `R$ ${topCategoryValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </span>
          </div>
        </div>

      </div>
    </div>

    <!-- Quick Action Pills Row -->
    <div class="home-quick-actions">
      <button class="home-action-pill primary-pill" id="home-btn-scan">
        <span class="material-symbols-outlined">qr_code_scanner</span>
        <span>Escanear NFC-e</span>
      </button>

      <button class="home-action-pill" id="home-btn-manual">
        <span class="material-symbols-outlined">add</span>
        <span>Inserir Manual</span>
      </button>

      ${authManager.isAdmin() ? `
        <button class="home-action-pill labs-pill" id="home-btn-ai-scanner" title="Iniciar cotação de preço no supermercado">
          <span class="material-symbols-outlined">auto_awesome</span>
          <span>Cotação de Preço</span>
        </button>
      ` : ''}
    </div>

    <!-- Radar de Preços do Bairro (Ao Vivo / Colaborativo) -->
    <div class="home-radar-sheet" style="margin-bottom: 1.25rem; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 1.25rem; padding: 1.15rem; box-shadow: var(--shadow-card);">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.85rem; flex-wrap: wrap; gap: 0.5rem;">
        <div style="display: flex; align-items: center; gap: 0.45rem; flex-wrap: wrap;">
          <span class="material-symbols-outlined" style="font-size: 22px; color: #10B981;">radar</span>
          <h3 style="margin: 0; font-size: 1.05rem; font-weight: 800; color: var(--text-main); white-space: nowrap;">
            Radar de Preços da Região
          </h3>
          <span style="background: rgba(16, 185, 129, 0.15); border: 1px solid rgba(16, 185, 129, 0.3); color: #10B981; font-size: 0.65rem; font-weight: 800; padding: 0.1rem 0.45rem; border-radius: var(--radius-pill); text-transform: uppercase; white-space: nowrap;">
            AO VIVO
          </span>
        </div>
        <a href="#produtos" style="font-size: 0.78rem; font-weight: 700; color: var(--color-primary-text, #2563EB); text-decoration: none; white-space: nowrap;">
          Ver Catálogo &rarr;
        </a>
      </div>

      <div id="home-radar-quotes-list" style="display: flex; flex-direction: column; gap: 0.45rem;">
        <div style="text-align: center; padding: 1rem; color: var(--text-muted); font-size: 0.8rem;">
          Carregando cotações recentes da comunidade...
        </div>
      </div>
    </div>

    <!-- Recent Purchases Sheet / Card -->
    <div class="home-recent-sheet">
      <div class="home-recent-header">
        <h3 class="home-recent-title">Compras Recentes</h3>
        <a href="#notas" class="home-recent-see-all">Ver todas (${notas.length})</a>
      </div>

      <div class="home-recent-list">
        ${recentNotas.length === 0 ? `
          <div class="home-empty-transactions">
            <span class="material-symbols-outlined home-empty-icon">receipt_long</span>
            <div class="home-empty-title">Nenhuma compra ainda</div>
            <p class="home-empty-desc">Escaneie o QR Code do seu cupom fiscal para ver suas despesas organizadas aqui.</p>
            <button class="home-action-pill primary-pill" id="home-btn-empty-scan" style="margin-top: 0.75rem;">
              <span class="material-symbols-outlined">qr_code_scanner</span>
              <span>Escanear Primeira Nota</span>
            </button>
          </div>
        ` : recentNotas.map(n => {
          const storeName = n.emitente?.razaoSocial || n.razaoSocial || 'Estabelecimento';
          const valor = (n.totais?.valorTotal || n.valorTotal || 0).toFixed(2).replace('.', ',');
          const qtd = n.totais?.qtdItens || n.qtdItens || (n.itens ? n.itens.length : 0);
          
          let dateStr = 'Data N/A';
          if (n.dataEmissao) {
            const d = new Date(n.dataEmissao);
            dateStr = d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
          }

          // Determine primary category or store icon
          let topItemCat = 'Outros';
          if (n.itens && n.itens.length > 0) {
            topItemCat = n.itens[0].categoriaNome || 'Outros';
          }
          const iconName = CATEGORY_ICON_NAME_MAP[topItemCat] || 'shopping_cart';
          const iconBg = CATEGORY_COLOR_MAP[topItemCat] ? `${CATEGORY_COLOR_MAP[topItemCat]}18` : '#F1F5F9';
          const iconColor = CATEGORY_COLOR_MAP[topItemCat] || '#334155';

          return `
            <div class="home-transaction-item" data-id="${n.id}" style="display: flex; flex-direction: column; align-items: stretch; gap: 0.4rem; padding: 0.85rem 0.5rem;">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <div class="home-transaction-icon-box" style="background-color: ${iconBg}; color: ${iconColor};">
                  <span class="material-symbols-outlined">${iconName}</span>
                </div>
                <h4 class="home-transaction-name" style="margin: 0; flex: 1; word-break: break-word; line-height: 1.25;">${escapeHTML(storeName)}</h4>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-left: 3.5rem;">
                <span class="home-transaction-date" style="flex: 1; margin-right: 0.5rem;">${escapeHTML(dateStr)} • ${qtd} ${qtd === 1 ? 'item' : 'itens'}</span>
                <span class="home-transaction-amount font-mono ${isBalanceHidden ? 'balance-hidden' : ''}" style="flex-shrink: 0;">
                  ${isBalanceHidden ? '••••••' : `- R$ ${valor}`}
                </span>
              </div>
            </div>
          `;
        }).join('')}
      </div>
    </div>

    <!-- Manual Note Entry Modal (Direct URL / Chave) -->
    <div id="home-manual-modal" class="modal-overlay hidden">
      <div class="modal-card">
        <div class="modal-header">
          <h3 class="modal-title">
            <span class="material-symbols-outlined" style="color: var(--color-primary);">add_circle</span>
            Adicionar Nota Manualmente
          </h3>
          <button class="modal-close-btn" id="btn-close-manual-modal">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <p class="modal-desc">Cole a URL completa da NFC-e ou a Chave de Acesso de 44 dígitos impressa no cupom:</p>
        
        <div class="modal-input-group">
          <input 
            type="text" 
            id="home-manual-url-input" 
            class="modal-input font-mono" 
            placeholder="https://www.nfce.fazenda.sp.gov.br/... ou chave 44 dígitos" 
          />
        </div>

        <div class="modal-actions">
          <button class="btn btn-secondary" id="btn-cancel-manual-modal">Cancelar</button>
          <button class="btn btn-primary" id="btn-submit-manual-modal">
            <span class="material-symbols-outlined">download_done</span>
            Processar Nota
          </button>
        </div>
      </div>
    </div>
  `;

  // === Event Listeners ===

  // Balance Visibility Toggle
  const toggleBalanceBtn = container.querySelector('#toggle-balance-visibility');
  const eyeIcon = container.querySelector('#balance-eye-icon');
  const totalBalanceEl = container.querySelector('#total-balance-value');
  const monthlySpendEl = container.querySelector('#monthly-spend-value');
  const avgTicketEl = container.querySelector('#avg-ticket-value');
  const topCatValEl = container.querySelector('#top-cat-value');

  if (toggleBalanceBtn) {
    toggleBalanceBtn.addEventListener('click', () => {
      isBalanceHidden = !isBalanceHidden;
      localStorage.setItem('nfce_hide_balance', isBalanceHidden.toString());
      if (eyeIcon) eyeIcon.textContent = isBalanceHidden ? 'visibility_off' : 'visibility';

      if (totalBalanceEl) {
        totalBalanceEl.textContent = isBalanceHidden 
          ? '••••••••' 
          : `R$ ${totalBalance.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        totalBalanceEl.classList.toggle('balance-hidden', isBalanceHidden);
      }
      if (monthlySpendEl) {
        monthlySpendEl.textContent = isBalanceHidden 
          ? '••••••••' 
          : `R$ ${monthlySpend.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        monthlySpendEl.classList.toggle('balance-hidden', isBalanceHidden);
      }
      if (avgTicketEl) {
        avgTicketEl.textContent = isBalanceHidden
          ? '••••••••'
          : `R$ ${avgPurchase.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        avgTicketEl.classList.toggle('balance-hidden', isBalanceHidden);
      }
      const priceHikeEl = container.querySelector('#price-hike-value');
      if (priceHikeEl) {
        priceHikeEl.textContent = isBalanceHidden
          ? '••••••••'
          : `+ R$ ${totalHikeDiff.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        priceHikeEl.classList.toggle('balance-hidden', isBalanceHidden);
      }

      container.querySelectorAll('.home-transaction-amount').forEach(el => {
        el.classList.toggle('balance-hidden', isBalanceHidden);
      });
    });
  }

  // Price Hike Card Navigation
  const hikeCard = container.querySelector('#home-card-price-hikes');
  if (hikeCard) {
    hikeCard.addEventListener('click', () => {
      window.location.hash = '#analise';
    });
  }

  // Scope Switcher
  const scopeSwitcher = container.querySelector('#home-scope-switcher');
  if (scopeSwitcher) {
    scopeSwitcher.querySelectorAll('.home-scope-pill').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetScope = btn.getAttribute('data-scope');
        if (targetScope !== currentScope) {
          setSavedScope(targetScope);
          renderHomeView(container, param, syncManager);
        }
      });
    });
  }

  // Global Scope Synchronization listener
  window.addEventListener('nfce-scope-changed', (e) => {
    const targetScope = e.detail?.scope;
    if (targetScope && targetScope !== currentScope) {
      renderHomeView(container, param, syncManager);
    }
  });

  // Quick Action Buttons
  const btnScan = container.querySelector('#home-btn-scan');
  if (btnScan) {
    btnScan.addEventListener('click', () => {
      window.location.hash = '#scanner';
    });
  }

  const btnEmptyScan = container.querySelector('#home-btn-empty-scan');
  if (btnEmptyScan) {
    btnEmptyScan.addEventListener('click', () => {
      window.location.hash = '#scanner';
    });
  }

  const btnAIScanner = container.querySelector('#home-btn-ai-scanner');
  if (btnAIScanner) {
    btnAIScanner.addEventListener('click', () => {
      window.location.hash = '#scanner-ai';
    });
  }

  // Manual Modal Interactions
  const manualModal = container.querySelector('#home-manual-modal');
  const btnManual = container.querySelector('#home-btn-manual');
  const btnCloseManual = container.querySelector('#btn-close-manual-modal');
  const btnCancelManual = container.querySelector('#btn-cancel-manual-modal');
  const btnSubmitManual = container.querySelector('#btn-submit-manual-modal');
  const manualInput = container.querySelector('#home-manual-url-input');

  const openManualModal = () => {
    if (manualModal) {
      manualModal.classList.remove('hidden');
      if (manualInput) {
        manualInput.value = '';
        manualInput.focus();
      }
    }
  };

  const closeManualModal = () => {
    if (manualModal) manualModal.classList.add('hidden');
  };

  if (btnManual) btnManual.addEventListener('click', openManualModal);
  if (btnCloseManual) btnCloseManual.addEventListener('click', closeManualModal);
  if (btnCancelManual) btnCancelManual.addEventListener('click', closeManualModal);

  if (btnSubmitManual && manualInput) {
    btnSubmitManual.addEventListener('click', async () => {
      const inputVal = manualInput.value.trim();
      if (!inputVal) {
        alert('Por favor, informe a URL ou a Chave de Acesso da NFC-e.');
        return;
      }

      btnSubmitManual.disabled = true;
      btnSubmitManual.innerHTML = `<span class="material-symbols-outlined spinning">sync</span> Processando...`;

      try {
        const payload = {
          url: inputVal.startsWith('http') ? inputVal : '',
          chave: !inputVal.startsWith('http') ? inputVal.replace(/\D/g, '') : '',
          scope: currentScope
        };

        const res = await authManager.fetchWithAuth('/api/scrape', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (res.ok) {
          const notaData = await res.json();
          await saveNotaLocal(notaData);
          closeManualModal();
          window.location.hash = `#nota/${notaData.id}`;
        } else {
          const errData = await res.json().catch(() => ({}));
          alert(`Erro ao processar cupom: ${errData.error || 'Verifique o endereço ou chave informada.'}`);
        }
      } catch (err) {
        alert(`Erro de conexão ao processar nota: ${err.message}`);
      } finally {
        btnSubmitManual.disabled = false;
        btnSubmitManual.innerHTML = `<span class="material-symbols-outlined">download_done</span> Processar Nota`;
      }
    });
  }

  // Click on recent transaction item navigates to detail
  container.querySelectorAll('.home-transaction-item').forEach(item => {
    item.addEventListener('click', () => {
      const id = item.getAttribute('data-id');
      if (id) {
        window.location.hash = `#nota/${id}`;
      }
    });
  });

  // Load Collaborative Radar Quotes
  async function loadRadarQuotes() {
    const radarList = container.querySelector('#home-radar-quotes-list');
    if (!radarList) return;

    try {
      const rawQuotes = await getRecentCotacoes(3);
      const recentQuotes = (rawQuotes || []).slice(0, 3);
      if (!recentQuotes || recentQuotes.length === 0) {
        radarList.innerHTML = `
          <div style="text-align: center; padding: 1.25rem 0.5rem; color: var(--text-muted); font-size: 0.82rem;">
            <span class="material-symbols-outlined" style="font-size: 28px; opacity: 0.4; display: block; margin-bottom: 0.35rem;">qr_code_scanner</span>
            Nenhuma cotação recente na região ainda.<br/>
            <a href="#scanner-ai" style="color: var(--color-primary-text, #2563EB); font-weight: 700; text-decoration: none; margin-top: 0.35rem; display: inline-block;">
              Escanear etiqueta ou produto &rarr;
            </a>
          </div>
        `;
        return;
      }

      radarList.innerHTML = recentQuotes.map(q => {
        const dateStr = q.createdAt || q.created_at ? new Date(q.createdAt || q.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : 'Recente';
        const isNfce = q.tipoFonte === 'nfce' || q.tipo_fonte === 'nfce';
        const sourceIcon = isNfce ? 'receipt_long' : 'crop_free';
        const sourceLabel = isNfce ? 'NFC-e' : 'Gôndola';

        return `
          <div class="radar-quote-item" style="display: flex; flex-direction: column; padding: 0.55rem 0.75rem; background: var(--bg-surface-alt); border-radius: 0.65rem; border: 1px solid var(--border-color); gap: 0.45rem;">
            <div style="font-size: 0.84rem; font-weight: 800; color: var(--text-main); line-height: 1.25;" title="${escapeHTML(q.descricao || 'Produto')}">
              ${escapeHTML(q.descricao || 'Produto')}
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; gap: 0.5rem;">
              <div style="font-size: 0.7rem; color: var(--text-muted); display: flex; align-items: center; gap: 0.35rem; flex-wrap: nowrap; overflow: hidden; flex: 1;">
                <span class="material-symbols-outlined" style="font-size: 13px; color: var(--color-primary-text, #2563EB); flex-shrink: 0;">store</span>
                <strong style="color: var(--text-subtle); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0;">${escapeHTML(q.mercado || 'Não informado')}</strong>
                <span style="white-space: nowrap; flex-shrink: 0;">• ${dateStr}</span>
                <span style="background: var(--bg-surface-dim); padding: 0.05rem 0.3rem; border-radius: 0.25rem; font-size: 0.65rem; display: inline-flex; align-items: center; gap: 0.15rem; flex-shrink: 0; white-space: nowrap;">
                  <span class="material-symbols-outlined" style="font-size: 10px;">${sourceIcon}</span>
                  ${sourceLabel}
                </span>
              </div>
              <div class="font-mono" style="font-size: 1.05rem; font-weight: 900; color: #10B981; flex-shrink: 0;">
                R$ ${q.preco.toFixed(2).replace('.', ',')}
              </div>
            </div>
          </div>
        `;
      }).join('');
    } catch (e) {
      console.warn('[HomeView] loadRadarQuotes error:', e);
      radarList.innerHTML = '<div style="font-size: 0.8rem; color: var(--text-muted); text-align: center;">Erro ao carregar radar.</div>';
    }
  }

  loadRadarQuotes();

  // Listen for background tab resume sync update
  const handleSyncUpdate = async () => {
    const currentHash = window.location.hash || '#home';
    const route = currentHash.split('/')[0].replace('#', '') || 'home';
    if (route !== 'home') {
      window.removeEventListener('datasyncupdated', handleSyncUpdate);
      return;
    }
    try {
      if (authManager.isAuthenticated()) {
        const res = await authManager.fetchWithAuth(`/api/notas?scope=${currentScope}&full=true`);
        if (res.ok) {
          const checkHash = window.location.hash || '#home';
          const checkRoute = checkHash.split('/')[0].replace('#', '') || 'home';
          if (checkRoute === 'home') {
            renderHomeView(container, param, syncManager);
          }
        }
      }
    } catch (e) {}
  };
  window.addEventListener('datasyncupdated', handleSyncUpdate);
}
